import type { MatrixPos, Task } from "@app/core";
import { clamp01, matrixIndexOf, matrixScore } from "@app/core";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

import type { QueueBand, ViewItem } from "./model.js";

const DRAG_SLOP_PX = 5;
/** Grace margin (px) around the grid inside which a release still places. */
export const DROP_MARGIN_PX = 28;

/** Queue bands that accept a chip drop (`data-queue-band`); "unranked" never
 *  does — taking a chip OFF the board is the tray zone's job. */
export type DropBand = Exclude<QueueBand, "unranked">;

export interface BoardDrag {
  id: string;
  pointerId: number;
  startX: number;
  startY: number;
  x: number;
  y: number;
  moved: boolean;
  /** Pointer currently over the tray / remove-from-board zone. */
  overZone: boolean;
  /** Load-strip day under the pointer (`data-strip-day` key), if any. */
  overDay: string | null;
  /** Queue band section under the pointer (`data-queue-band`), if any. */
  overBand: DropBand | null;
  /** Pointer is within the board's exact rect or its forgiving drop margin. */
  overBoard: boolean;
  /** Grid rect captured at drag start (stable for the gesture). */
  rect: DOMRect;
}

interface Options {
  gridRef: RefObject<HTMLDivElement | null>;
  itemOf: (id: string) => ViewItem | undefined;
  onPlace: (item: ViewItem, pos: MatrixPos) => void;
  onUnplace: (item: ViewItem) => void;
  onOpen: (task: Task) => void;
  onDropOnDay: (item: ViewItem, dayKey: string, at: { x: number; y: number }) => void;
  onHoverDay: (dayKey: string | null) => void;
  onDropOnBand: (item: ViewItem, band: DropBand) => void;
  onHoverBand: (band: DropBand | null) => void;
  /** Mirrors whether a chip drag is in flight (e.g. so empty queue bands can
   *  materialize as drop targets while one is). */
  onDragActive: (active: boolean) => void;
}

export function posFromPoint(x: number, y: number, rect: DOMRect): MatrixPos {
  return {
    urgency: clamp01((x - rect.left) / rect.width),
    importance: clamp01(1 - (y - rect.top) / rect.height),
  };
}

export function overBoardAt(x: number, y: number, rect: DOMRect): boolean {
  return (
    x >= rect.left - DROP_MARGIN_PX && x <= rect.right + DROP_MARGIN_PX &&
    y >= rect.top - DROP_MARGIN_PX && y <= rect.bottom + DROP_MARGIN_PX
  );
}

/** First `data-*` attribute value in the hit-test stack under the pointer. The
 *  dragged chip itself can sit right under the cursor (tray chips track it
 *  exactly), so scan the whole stack instead of only the topmost element. */
export function targetsAtPoint(x: number, y: number, ignoredRoot?: HTMLElement | null): {
  day: string | null;
  band: DropBand | null;
  overZone: boolean;
} {
  let day: string | null = null;
  let band: DropBand | null = null;
  let overZone = false;
  for (const el of document.elementsFromPoint(x, y)) {
    const html = el as HTMLElement;
    // A tray chip remains a DOM descendant of the tray after CSS-translating
    // over the board. Ignore its visual subtree or `closest()` would report the
    // tray as armed everywhere the chip travels.
    if (ignoredRoot?.contains(html)) continue;
    day ??= html.closest?.("[data-strip-day]")?.getAttribute("data-strip-day") ?? null;
    const bandValue = html.closest?.("[data-queue-band]")?.getAttribute("data-queue-band");
    if (!band && (bandValue === "now" || bandValue === "next" || bandValue === "later")) {
      band = bandValue;
    }
    overZone ||= !!html.closest?.("[data-matrix-tray-zone]");
    if (day && band && overZone) break;
  }
  return { day, band, overZone };
}

/** React only needs a new drag snapshot when rendered meaning changes. Raw
 * pointer coordinates move the chip imperatively on the compositor instead. */
export function dragRenderChanged(previous: BoardDrag, next: BoardDrag): boolean {
  if (
    previous.moved !== next.moved ||
    previous.overZone !== next.overZone ||
    previous.overDay !== next.overDay ||
    previous.overBand !== next.overBand ||
    previous.overBoard !== next.overBoard
  ) return true;
  if (!next.overBoard) return false;
  const previousPos = posFromPoint(previous.x, previous.y, previous.rect);
  const nextPos = posFromPoint(next.x, next.y, next.rect);
  return (
    matrixIndexOf(previousPos.urgency) !== matrixIndexOf(nextPos.urgency) ||
    matrixIndexOf(previousPos.importance) !== matrixIndexOf(nextPos.importance) ||
    matrixIndexOf(matrixScore(previousPos)) !== matrixIndexOf(matrixScore(nextPos))
  );
}

type FrameScheduler = (callback: FrameRequestCallback) => number;

/** Preserve the compositor-translated source until React has committed the
 * optimistic placement. Clearing it synchronously in `pointerup` exposes the
 * old source position for one render, which looks like drop latency. */
export function releaseDragRootAfterCommit(
  root: HTMLElement,
  schedule: FrameScheduler = window.requestAnimationFrame.bind(window),
): number {
  return schedule(() => {
    root.style.removeProperty("translate");
    root.style.removeProperty("will-change");
  });
}

/**
 * Pointer-drag gesture for board/tray chips with a FOUR-target drop, resolved
 * in priority order from the RELEASE coordinates (the drag state can lag a
 * frame): a load-strip day (schedule it there), a queue band section (coarse
 * placement at the band's diagonal center), the tray zone (unplace a placed
 * chip), or the grid ± margin (exact placement). A press that never crosses the
 * slop opens the editor instead; a cancelled pointer commits nothing.
 */
export function useBoardDrag({
  gridRef, itemOf, onPlace, onUnplace, onOpen,
  onDropOnDay, onHoverDay, onDropOnBand, onHoverBand, onDragActive,
}: Options) {
  const [drag, setDrag] = useState<BoardDrag | null>(null);
  // State drives semantic feedback; the ref is the authoritative live gesture
  // so release cannot observe render-lagged coordinates or `moved` state.
  const dragRef = useRef<BoardDrag | null>(null);
  // The dragged wrapper is translated directly. This avoids sending 60-240
  // pointer events/sec through the whole React/Framer board tree.
  const dragRootRef = useRef<HTMLElement | null>(null);
  const releasedRootRef = useRef<HTMLElement | null>(null);
  const releaseFrameRef = useRef<number | null>(null);
  // The three pointer handlers are handed to EVERY chip, so they must keep a
  // stable identity or no chip can memoize. Options change per render; the ref
  // keeps the live ones without re-creating the handlers.
  const optsRef = useRef<Options>({ gridRef, itemOf, onPlace, onUnplace, onOpen, onDropOnDay, onHoverDay, onDropOnBand, onHoverBand, onDragActive });
  optsRef.current = { gridRef, itemOf, onPlace, onUnplace, onOpen, onDropOnDay, onHoverDay, onDropOnBand, onHoverBand, onDragActive };

  function moveDragRoot(x: number, y: number, drag: BoardDrag) {
    const root = dragRootRef.current;
    if (!root) return;
    root.style.translate = `${x - drag.startX}px ${y - drag.startY}px`;
  }

  function clearRootStyle(root: HTMLElement | null) {
    if (root) {
      root.style.removeProperty("translate");
      root.style.removeProperty("will-change");
    }
  }

  function flushReleasedRoot() {
    if (releaseFrameRef.current !== null) {
      window.cancelAnimationFrame(releaseFrameRef.current);
      releaseFrameRef.current = null;
    }
    clearRootStyle(releasedRootRef.current);
    releasedRootRef.current = null;
  }

  function clearDragRoot() {
    clearRootStyle(dragRootRef.current);
    dragRootRef.current = null;
  }

  function finishDrag() {
    clearDragRoot();
    setDrag(null);
  }

  function finishCommittedDrop() {
    flushReleasedRoot();
    const root = dragRootRef.current;
    dragRootRef.current = null;
    setDrag(null);
    if (!root) return;
    releasedRootRef.current = root;
    releaseFrameRef.current = releaseDragRootAfterCommit(root, (callback) =>
      window.requestAnimationFrame((time) => {
        releasedRootRef.current = null;
        releaseFrameRef.current = null;
        callback(time);
      }),
    );
  }

  const onPointerDown = useCallback(function onPointerDown(e: React.PointerEvent, id: string) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // One chip owns the gesture. A second touch must not replace or later end
    // the captured pointer's drag.
    if (dragRef.current) return;
    flushReleasedRoot();
    const rect = optsRef.current.gridRef.current?.getBoundingClientRect();
    if (!rect) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* pointer already released — the gesture still degrades to a click */ }
    dragRootRef.current = (e.currentTarget as HTMLElement).closest<HTMLElement>("[data-matrix-drag-root]");
    if (dragRootRef.current) dragRootRef.current.style.willChange = "translate";
    const next = {
      id,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      moved: false,
      overZone: false,
      overDay: null,
      overBand: null,
      overBoard: false,
      rect,
    };
    dragRef.current = next;
  }, []);

  const onPointerMove = useCallback(function onPointerMove(e: React.PointerEvent, id: string) {
    const { clientX: x, clientY: y } = e;
    const d = dragRef.current;
    if (!d || d.id !== id || d.pointerId !== e.pointerId) return;
    const moved = d.moved || Math.hypot(x - d.startX, y - d.startY) > DRAG_SLOP_PX;
    if (moved) moveDragRoot(x, y, d);
    // Hit tests are meaningful only after the gesture crosses the slop. This
    // keeps ordinary pointer hover over chips off the document hit-test path.
    const insideBoard = (
      x >= d.rect.left && x <= d.rect.right &&
      y >= d.rect.top && y <= d.rect.bottom
    );
    // The board cannot overlap the strip/queue/tray. Stay entirely on cached
    // geometry while inside it; elsewhere one stack scan resolves all external
    // targets instead of doing two document hit tests plus a layout read.
    const targets = moved && !insideBoard
      ? targetsAtPoint(x, y, dragRootRef.current)
      : { day: null, band: null, overZone: false };
    const day = targets.day;
    const band = !day ? targets.band : null;
    const overZone = moved && !day && !band && targets.overZone;
    const overBoard = moved && !day && !band && !overZone && overBoardAt(x, y, d.rect);
    const next = { ...d, x, y, moved, overZone, overDay: day, overBand: band, overBoard };
    dragRef.current = next;
    if (dragRenderChanged(d, next)) setDrag(next);
  }, []);

  const onPointerEnd = useCallback(function onPointerEnd(e: React.PointerEvent, id: string) {
    const { itemOf, onOpen, onDropOnDay, onDropOnBand, onUnplace, onPlace } = optsRef.current;
    const d = dragRef.current;
    if (!d || d.id !== id || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    const item = itemOf(id);
    if (!item) {
      finishDrag();
      return;
    }
    const moved = d.moved || Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > DRAG_SLOP_PX;
    if (!moved) {
      finishDrag();
      if (e.type !== "pointercancel") onOpen(item.task);
      return;
    }
    if (e.type === "pointercancel") {
      finishDrag();
      return;
    }
    const { clientX: x, clientY: y } = e;
    // The translated chip is still a DOM descendant of its source container.
    // Exclude it here just like live hover hit-testing does, otherwise a tray
    // chip released over the board reports the tray as its drop target.
    const targets = targetsAtPoint(x, y, dragRootRef.current);
    const day = targets.day;
    if (day) {
      onDropOnDay(item, day, { x, y });
      finishDrag();
      return;
    }
    const band = targets.band;
    if (band) {
      onDropOnBand(item, band);
      finishCommittedDrop();
      return;
    }
    if (targets.overZone) {
      if (item.placed) {
        onUnplace(item);
        finishCommittedDrop();
      } else {
        finishDrag();
      }
      return;
    }
    if (overBoardAt(x, y, d.rect)) {
      onPlace(item, posFromPoint(x, y, d.rect));
      finishCommittedDrop();
      return;
    }
    finishDrag();
  }, []);

  const dragging = drag?.moved ? drag : null;
  const overDay = dragging?.overDay ?? null;
  const overBand = dragging?.overBand ?? null;
  const active = !!dragging;
  // Mirror the hovered targets up so the strip / queue can highlight them. These
  // fire on the VALUES only — the callbacks are read from the ref, so a caller
  // that re-creates them per render can't retrigger the mirror.
  useEffect(() => {
    optsRef.current.onHoverDay(overDay);
  }, [overDay]);
  useEffect(() => {
    optsRef.current.onHoverBand(overBand);
  }, [overBand]);
  useEffect(() => {
    optsRef.current.onDragActive(active);
  }, [active]);
  useEffect(() => () => {
    flushReleasedRoot();
    clearDragRoot();
    optsRef.current.onHoverDay(null);
    optsRef.current.onHoverBand(null);
    optsRef.current.onDragActive(false);
  }, []);

  return { dragging, onPointerDown, onPointerMove, onPointerEnd };
}
